import { useMemo, useState } from 'react';
import { SearchSuggest } from '@skupreme/shared/ui';
import { ActiveText, MuteText } from '@elements/Common';
import { catalogAutocompleteHook, isValidCatalogSearch } from '@hooks/catalog';
import { useDebounce } from '@hooks/debounce';

/* Semantic's `minCharacters`: shorter queries never showed results. */
const MIN_CHARACTERS = 4;

const INFO_TEXT =
  'sk:inline-block sk:pt-2 sk:[&_strong]:text-fg-tertiary sk:[&_strong]:font-semibold';

export function CatalogResultRenderer({
  search,
  _id,
  p_title,
  p_sku,
  upc,
  secondary_identifier,
  state,
}) {
  const showResult = string => {
    try {
      const highlighted = string.replace(
        new RegExp(search, 'gi'),
        d => `<strong>${d}</strong>`,
      );

      return <span dangerouslySetInnerHTML={{ __html: highlighted }} />;
    } catch (error) {
      return string;
    }
  };

  return (
    <div className="sk:flex sk:w-full sk:min-w-0 sk:flex-col">
      <div className="sk:flex sk:items-center sk:justify-between sk:gap-2">
        <div className="sk:min-w-0">
          <ActiveText
            title={p_title}
            className="sk:block sk:truncate sk:[&>span]:block sk:[&>span]:max-w-62.5 sk:[&>span]:whitespace-normal sk:[&>span]:break-words sk:md:[&>span]:max-w-75"
          >
            {showResult(p_title)}
          </ActiveText>
          <MuteText title={p_sku} className={INFO_TEXT}>
            {showResult(p_sku)}
          </MuteText>
        </div>
        {state && (
          <MuteText title={state} className="sk:truncate">
            {showResult(state)}
          </MuteText>
        )}
      </div>
      {(upc || secondary_identifier) && (
        <div className="sk:flex sk:items-center sk:justify-between sk:gap-2">
          {upc && (
            <MuteText title={upc} className={INFO_TEXT}>
              <strong>UPC:</strong> {showResult(upc)}
            </MuteText>
          )}
          {secondary_identifier && (
            <MuteText title={secondary_identifier} className={INFO_TEXT}>
              <strong>Secondary Identifier:</strong>{' '}
              {showResult(secondary_identifier)}
            </MuteText>
          )}
        </div>
      )}
    </div>
  );
}

type CatalogSuggestion = {
  _id: string;
  p_title?: string;
  p_sku?: string;
  upc?: string;
  secondary_identifier?: string;
  state?: string;
  search?: string;
};

type CatalogSuggestionRow = CatalogSuggestion & { id: string };

type CatalogSearchInputProps = {
  regionId?: string | null;
  /** Opens the results above the field, for a field near the bottom. */
  top?: boolean;
  onResultClick: (catalog: CatalogSuggestion) => void;
  inputClassName?: string;
  /** Mirrors the typed text into the caller's table filter. */
  setTableSearch?: (search: string) => void;
};

export function CatalogSearchInput({
  regionId,
  top,
  onResultClick,
  inputClassName,
  setTableSearch = () => {},
}: CatalogSearchInputProps) {
  const [search, setSearch] = useState('');

  const finalSearch = useDebounce(search, 1000, 1);

  const { data, loading } = catalogAutocompleteHook(finalSearch, regionId);

  const pending = finalSearch !== search && search.length > 3;
  const searchable =
    isValidCatalogSearch(search) && search.length >= MIN_CHARACTERS;

  const items = useMemo<CatalogSuggestionRow[]>(
    () =>
      searchable && finalSearch === search
        ? (data as CatalogSuggestion[]).map(result => ({
            ...result,
            id: result._id,
          }))
        : [],
    [data, searchable, finalSearch, search],
  );

  const changeSearch = (value: string) => {
    setSearch(value);
    setTableSearch(value);
  };

  const select = (result: CatalogSuggestion) => {
    if (result?._id) {
      onResultClick(result);
      setSearch('');
    }
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  return (
    <SearchSuggest<CatalogSuggestionRow>
      label="Search catalog"
      placeholder="SKU, UPC, Title, ASIN..."
      value={search}
      onValueChange={changeSearch}
      items={items}
      onSelect={select}
      isLoading={loading || pending}
      emptyText={
        searchable
          ? 'No results found.'
          : `Type at least ${MIN_CHARACTERS} characters`
      }
      placement={top ? 'top' : 'bottom'}
      className={inputClassName}
    >
      {item => (
        <CatalogResultRenderer
          search={item.search}
          _id={item._id}
          p_title={item.p_title}
          p_sku={item.p_sku}
          upc={item.upc}
          secondary_identifier={item.secondary_identifier}
          state={item.state}
        />
      )}
    </SearchSuggest>
  );
}
